import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Refund() {
    return (
        <div className="bg-ink-900 text-cream min-h-screen px-6 py-16">
            <div className="container-prose max-w-2xl mx-auto" data-testid="refund-page">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-cream-muted hover:text-clay mb-10 transition"
                    data-testid="refund-back-link"
                >
                    <ArrowLeft size={14} /> Back to the guide
                </Link>

                <p className="label-eyebrow mb-3">Restful Press</p>
                <h1 className="heading-display text-4xl md:text-5xl mb-8">Refund Policy</h1>
                <p className="text-sm text-cream-muted/70 mb-12">Last updated: February 2026</p>

                <div className="prose-content space-y-6 text-cream-muted leading-relaxed">
                    <p>
                        We want this guide to actually help. If it doesn't, you should not be
                        out of pocket.
                    </p>

                    <h2 className="font-serif text-2xl text-cream mt-10 mb-2">Our promise</h2>
                    <p>
                        Every purchase is covered by a simple <strong className="text-cream">30-day refund</strong>{" "}
                        guarantee. If <em>The 7-Day Sleep Reset</em> hasn't done what we promised
                        for you, write to us within 30 days of your purchase and tell us{" "}
                        <em className="text-cream">what didn't work</em>. Your honest feedback helps us make
                        the guide better for the next reader — and helps us refund you quickly.
                    </p>

                    <h2 className="font-serif text-2xl text-cream mt-10 mb-2">How to request a refund</h2>
                    <p>
                        Send a short email to{" "}
                        <a href="mailto:hello@restfulpress.com" className="text-clay hover:underline">
                            hello@restfulpress.com
                        </a>{" "}
                        from the address you used at checkout. Please include:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-cream-muted">
                        <li>Your payment reference (the <code className="text-clay">rp_…</code> code from your receipt)</li>
                        <li>One or two sentences on which day didn&apos;t work for you and why</li>
                    </ul>
                    <p>
                        We process refunds within five business days through the same payment
                        method.
                    </p>

                    <h2 className="font-serif text-2xl text-cream mt-10 mb-2">A note on the guide itself</h2>
                    <p>
                        Every PDF we send is stamped with the buyer&apos;s email address and order
                        reference on every page. This is how we protect the small team that
                        made it. By requesting a refund you agree to{" "}
                        <strong className="text-cream">delete all copies of the PDF you have downloaded</strong>{" "}
                        and to not redistribute it. Retaining or sharing a refunded copy is
                        copyright infringement under Nigerian law and international treaties,
                        and we reserve the right to pursue chargebacks and legal remedies for
                        clear abuse.
                    </p>
                    <p className="text-sm text-cream-muted/80">
                        None of this applies to honest refunds — the vast majority of readers
                        never trigger it. It exists only to keep the guide affordable for
                        everyone else.
                    </p>

                    <h2 className="font-serif text-2xl text-cream mt-10 mb-2">Add-on products</h2>
                    <p>
                        The Audio Companion and any future add-on products carry the same
                        30-day refund guarantee. Refunding an add-on does not affect your
                        access to the main guide.
                    </p>

                    <h2 className="font-serif text-2xl text-cream mt-10 mb-2">A small note</h2>
                    <p>
                        We are a tiny team. Refunds are processed manually by a real person, so
                        please give us a couple of days to respond. We always do.
                    </p>

                    <h2 className="font-serif text-2xl text-cream mt-10 mb-2">Contact</h2>
                    <p>
                        Questions about a purchase? Email{" "}
                        <a href="mailto:hello@restfulpress.com" className="text-clay hover:underline">
                            hello@restfulpress.com
                        </a>{" "}
                        and we will look after you.
                    </p>
                </div>
            </div>
        </div>
    );
}
